import { createContext, useContext, useEffect, useState, ReactNode } from 'react';

export type Theme = 'light' | 'dark' | 'carnaval' | 'glass';
const STORAGE_KEY = 'lm-theme';
const THEMES: Theme[] = ['light', 'dark', 'carnaval'];
/* 'glass' NO entra al ciclo público: es beta gateada a la cuenta de Carlos
   (ThemeToggle lo suma al ciclo solo para él; AppLayout lo normaliza si otro
   usuario lo trae en localStorage). Sí es válido al restaurar de storage. */
const VALID: Theme[] = [...THEMES, 'glass'];

interface ThemeState {
  theme: Theme;
  themes: Theme[];
  toggle: () => void;             // cicla light → dark → carnaval → light
  setTheme: (t: Theme) => void;
}

const ThemeContext = createContext<ThemeState | undefined>(undefined);

/** Resuelve el tema inicial: preferencia guardada, si no OSCURO por defecto
    (el amarillo de marca luce mejor sobre oscuro — decidido con Carlos). */
function initialTheme(): Theme {
  try {
    const saved = localStorage.getItem(STORAGE_KEY) as Theme | null;
    if (saved && VALID.includes(saved)) return saved;
  } catch { /* localStorage no disponible */ }
  return 'dark';
}

/** carnaval = dark + carnaval · glass = dark + glass (ambos heredan los
    arreglos del modo oscuro y sobreescriben encima). */
function applyTheme(t: Theme) {
  const cl = document.documentElement.classList;
  cl.toggle('dark', t === 'dark' || t === 'carnaval' || t === 'glass');
  cl.toggle('carnaval', t === 'carnaval');
  cl.toggle('glass', t === 'glass');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);

  useEffect(() => {
    applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* no-op */ }
  }, [theme]);

  const setTheme = (t: Theme) => setThemeState(t);
  const toggle = () => setThemeState((t) => THEMES[(THEMES.indexOf(t) + 1) % THEMES.length]);

  return (
    <ThemeContext.Provider value={{ theme, themes: THEMES, toggle, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de <ThemeProvider>');
  return ctx;
}
